import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { distinctUntilChanged, map } from 'rxjs';
import { Measurement, SbcApiService } from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';

type UnifiedCompareRow = {
  field: string;
  unit?: string;
  measured: number | string | null;
  plan: number | string | null;
  planStatus?: string;
  planTolerance?: number | string | null;
  byeLawLimit: number | string | null;
  byeLawStatus?: string;
  byeLawTolerance?: number | string | null;
};

type RuleCheckResponse = {
  alreadyChecked?: boolean;
  existingRunId?: number;
  message?: string;
  run?: { id: number };
};

type QueryParamsState = {
  locationId: string;
  plotRef: string;
  compare: string;
};

import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';

@Component({
  selector: 'app-measurements',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './measurements.component.html',
  styleUrl: './measurements.component.css',
})
export class MeasurementsComponent {
  private readonly api = inject(SbcApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly session = inject(SessionService);

  rows = signal<Measurement[]>([]);
  error = signal('');
  toast = signal('');
  loading = signal(false);
  ruleCheckLoading = signal(false);
  compareLoading = signal(false);
  filterLocationId = signal<number | null>(null);
  selectedPlotRef = signal('');
  compareVerdict = signal('');
  compareRows = signal<UnifiedCompareRow[]>([]);
  activeTab = signal<'compare' | 'all'>('all');
  comparedPlotRefs = signal<Set<string>>(new Set());

  private loadSeq = 0;
  private compareSeq = 0;

  readonly plotOptions = computed(() =>
    [...this.rows()]
      .map((r) => String(r.plotRef || '').trim())
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort((a, b) => a.localeCompare(b))
  );

  constructor() {
    this.syncComparedFromStorage();

    this.route.queryParamMap
      .pipe(
        map((params) => ({
          locationId: params.get('locationId') ?? '',
          plotRef: params.get('plotRef') ?? '',
          compare: params.get('compare') ?? '',
        })),
        distinctUntilChanged(
          (a, b) =>
            a.locationId === b.locationId && a.plotRef === b.plotRef && a.compare === b.compare
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((params) => this.applyRouteParams(params));
  }

  isCompared(plotRef: string | number | undefined): boolean {
    return this.comparedPlotRefs().has(String(plotRef || '').trim());
  }

  private comparedStorageKey(): string {
    return `sbc-measurements-compared-${this.session.authorityId()}`;
  }

  private syncComparedFromStorage(): void {
    this.comparedPlotRefs.set(this.loadComparedFromStorage());
  }

  private loadComparedFromStorage(): Set<string> {
    try {
      const raw = sessionStorage.getItem(this.comparedStorageKey());
      const list = raw ? (JSON.parse(raw) as string[]) : [];
      return new Set(list.map((v) => String(v).trim()).filter(Boolean));
    } catch {
      return new Set();
    }
  }

  private saveComparedToStorage(refs: Set<string>): void {
    sessionStorage.setItem(this.comparedStorageKey(), JSON.stringify([...refs]));
  }

  private markPlotCompared(plotRef: string): void {
    const ref = plotRef.trim();
    if (!ref) return;
    const next = new Set(this.comparedPlotRefs());
    next.add(ref);
    this.comparedPlotRefs.set(next);
    this.saveComparedToStorage(next);
  }

  private applyRouteParams(params: QueryParamsState): void {
    const locId = params.locationId ? Number(params.locationId) : null;
    this.filterLocationId.set(locId != null && Number.isFinite(locId) ? locId : null);

    if (params.plotRef) {
      this.selectedPlotRef.set(params.plotRef);
    }
    if (params.compare === '1') {
      this.activeTab.set('compare');
    }

    this.reload({
      autoCompare: params.compare === '1' && !!params.plotRef,
      markCompared: params.compare === '1' && !!params.plotRef,
    });
  }

  setTab(tab: 'compare' | 'all'): void {
    this.activeTab.set(tab);
    if (tab === 'compare' && this.selectedPlotRef() && this.rows().length && !this.compareRows().length) {
      this.loadCompare();
    }
  }

  reload(options?: { autoCompare?: boolean; markCompared?: boolean }): void {
    const seq = ++this.loadSeq;
    this.loading.set(true);
    this.error.set('');

    this.api.listMeasurements().subscribe({
      next: (res) => {
        if (seq !== this.loadSeq) return;

        let rows = res.data || [];
        const locId = this.filterLocationId();
        if (locId != null) {
          rows = rows.filter((r) => Number(r.locationId) === locId);
        }
        rows = rows.filter((r) => r.statusCd === 'final');
        rows.sort((a, b) => String(a.plotRef).localeCompare(String(b.plotRef)));
        this.rows.set(rows);

        const selected = this.selectedPlotRef();
        if (!selected && rows.length) {
          this.selectedPlotRef.set(String(rows[0].plotRef));
        } else if (selected && !rows.some((r) => String(r.plotRef) === selected) && rows.length) {
          this.selectedPlotRef.set(String(rows[0].plotRef));
        }

        this.loading.set(false);

        if (options?.autoCompare && this.activeTab() === 'compare' && this.selectedPlotRef()) {
          this.loadCompare({ markCompared: options.markCompared });
        }
      },
      error: (err) => {
        if (seq !== this.loadSeq) return;
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load measurements');
      },
    });
  }

  onComparePlotPick(plotRef: string): void {
    const ref = String(plotRef || '').trim();
    if (!ref || ref === this.selectedPlotRef()) return;
    this.selectPlot(ref);
  }

  selectPlot(plotRef: string): void {
    const ref = String(plotRef || '').trim();
    if (!ref) {
      this.showToast('Select a plot to compare');
      return;
    }

    this.activeTab.set('compare');
    if (ref === this.selectedPlotRef()) {
      this.loadCompare({ markCompared: true });
      return;
    }

    this.selectedPlotRef.set(ref);
    this.compareRows.set([]);
    this.compareVerdict.set('');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { plotRef: ref, compare: '1' },
      queryParamsHandling: 'merge',
    });
  }

  loadCompare(options?: { markCompared?: boolean }): void {
    const plotRef = this.selectedPlotRef().trim();
    if (!plotRef) {
      this.showToast('Select a plot to compare');
      return;
    }

    const seq = ++this.compareSeq;
    this.compareLoading.set(true);
    this.compareRows.set([]);
    this.compareVerdict.set('');

    this.api.compareMeasurement(plotRef).subscribe({
      next: (res) => {
        if (seq !== this.compareSeq) return;
        const data = res.data as {
          compare?: { verdict?: string; status?: string };
          unifiedRows?: UnifiedCompareRow[];
        };
        this.compareVerdict.set(
          String(data?.compare?.verdict || data?.compare?.status || 'Comparison complete')
        );
        this.compareRows.set(data?.unifiedRows || []);
        if (options?.markCompared) {
          this.markPlotCompared(plotRef);
        }
        this.compareLoading.set(false);
      },
      error: (err) => {
        if (seq !== this.compareSeq) return;
        this.compareLoading.set(false);
        this.showToast(err?.error?.message || 'Compare failed');
      },
    });
  }

  formatValue(value: number | string | null | undefined, unit = ''): string {
    if (value == null || value === '') return '—';
    return `${value}${unit ? ` ${unit}` : ''}`;
  }

  statusClass(status: string | undefined | null): string {
    const key = String(status || '').toLowerCase();
    if (key === 'pass' || key === 'ok' || key === 'match') return 'compare-pass';
    if (key === 'fail' || key === 'breach' || key === 'mismatch') return 'compare-fail';
    if (key === 'n/a' || key === 'skipped') return 'compare-na';
    return 'compare-neutral';
  }

  runRuleCheck(): void {
    const locId = this.filterLocationId();
    if (locId == null) {
      this.showToast('Open a location from LiDAR Survey or add ?locationId= to the URL');
      return;
    }
    if (this.ruleCheckLoading()) return;

    this.ruleCheckLoading.set(true);
    this.api
      .startRuleCheck({
        locationId: locId,
        runLabel: `Measurements UI · ${new Date().toISOString()}`,
      })
      .subscribe({
        next: (res) => {
          this.ruleCheckLoading.set(false);
          const data = (res.data || {}) as RuleCheckResponse;
          const meta = res.meta as { alreadyChecked?: boolean } | undefined;
          if (data.alreadyChecked || meta?.alreadyChecked) {
            const runId = data.existingRunId || data.run?.id;
            this.showToast(data.message || 'Already checked against bye-law norms');
            if (runId) {
              void this.router.navigate(['/enforcement/compliance-runs'], {
                queryParams: { highlightRun: runId },
              });
            }
            return;
          }
          this.showToast('Bye-law rule check completed');
          void this.router.navigate(['/enforcement/compliance-runs']);
        },
        error: (err) => {
          this.ruleCheckLoading.set(false);
          this.showToast(err?.error?.message || 'Rule check failed');
        },
      });
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }
}
